import * as React from 'react';
import { observable, computed } from "mobx";
import { observer } from "mobx-react";
import { resolve } from "inversify-react";
import autobind from "autobind-decorator";
import { v4 } from "uuid";

import { ContextService } from 'src/core/services/context.service';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { HttpService } from 'src/core/services/http.service';
import { ContractFormView } from "./contract-form.view";
import { EntitySchemaDataService } from 'src/app/general/entity-schemas/entity-schema.data';

interface ContractFormControllerProps extends ComponentViewProps<ContractItem> {
	mode: ControlMode;
	onModeChanged: (mode: ControlMode) => void;
	contextService: ContextService;
	formatService: any;
	product: ProductItem | TourItineraryItem;
	currencies: CurrencySummary[];
	defaultLanguaje: string;
	breadcrumb?: any[];
	identityService: any;
	isDirty: boolean;
}

interface ContractFormControllerState {
	isValidGeneralInfo: boolean;
	schema?: JsonSchema;
}

@observer
export class ContractFormController extends React.Component<ContractFormControllerProps, ContractFormControllerState> {
	@resolve
	http: HttpService;

	@resolve
	toast: ToastNotifications;

	@observable
	selectedMarket: number = 0;

	constructor(props: Readonly<ContractFormControllerProps>) {
		super(props);

		this.state = {
			isValidGeneralInfo: false
		};
	}
	
	componentDidMount() {
		this.props.setBusy(true);

		this.setState({ isValidGeneralInfo: !this.isNew() });
		Promise.all([this.loadSchema()])
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				this.props.setBusy(false);
			});
	}

	componentWillReceiveProps(nextProps: Readonly<ContractFormControllerProps>) {
		if (nextProps.model) {
			this.handleGeneralInfoValidationChecking(nextProps.model);
		}
	}

	private async loadSchema(): Promise<void> {
		const entitySchemaService = EntitySchemaDataService.make();

		entitySchemaService.addFilter('name', 'eq', 'Contract');
		entitySchemaService.addSorting('name', 'asc', true);
		entitySchemaService.pageSize = 1;

		const entitySchemas = await entitySchemaService.load(query => query);
		const schema = ObjectUtils.toObject<JsonSchema>(entitySchemas[0].schema);
		this.setState({ schema: schema });
	}

	private isNew(): boolean {
		return this.props.model == null || this.props.model.id < 0;
	}

	@computed
	get market() {
		return this.selectedMarket == 0 ? null : this.selectedMarket;
	}

	handleGeneralInfoValidationChecking = (contract: ContractItem) => {
		const isDefinedSupplier = contract.supplierId != null;
		const isDefinedDisplayName = contract.dynamicProperties.displayName && contract.dynamicProperties.displayName['en'] && contract.dynamicProperties.displayName['en'].trim() != '';
		this.setState({
			isValidGeneralInfo: isDefinedSupplier && isDefinedDisplayName
		});
	};

	@autobind
	protected async generatePrices(priceCurrency: CurrencySummary) {
		const contract = this.props.model;
		const cost: CostItem = contract.dynamicProperties['cost'];
		const price: PriceItem = contract.dynamicProperties['price'];
		// Copy structure from Cost to Sell
		price.structure.crossingDateBandBehaviour = cost.structure.crossingDateBandBehaviour;
		price.structure.type = cost.structure.type;
		price.structure.maxPax = cost.structure.maxPax;
		price.structure.isCustomOccupancyConfig = cost.structure.isCustomOccupancyConfig;
		price.structure.mode = cost.structure.mode;
		price.structure.required = cost.structure.required;
		price.structure.sign = cost.structure.sign;
		price.structure.customOccupancyConfigs = cost.structure.customOccupancyConfigs.map((o) => {
			const occupancy = {
				id: v4(),
			};
			const ageBands = contract == null ? [] : contract.dynamicProperties['ageBands'].map((e: AgeBandItem) => e.label);
			for (let name = 0; name < ageBands.length; name++) {
				const prop = ageBands[name].toLocaleLowerCase();
				occupancy[prop] = o[prop].toString();
			}
			return occupancy;
		});

		const items: PriceElement[] = cost.elements.map(e => ({
			uuid: e.uuid,
			minPax: e.minPax,
			maxPax: e.maxPax,
			minAge: e.minAge,
			maxAge: e.maxAge,
			minDuration: e.minDuration,
			maxDuration: e.maxDuration,
			type: e.type,
			service: e.service,
			market: this.market,
			currency: priceCurrency.id,
			bookingDateFrom: e.bookingDateFrom,
			bookingDateTo: e.bookingDateTo,
			serviceDateFrom: e.serviceDateFrom,
			serviceDateTo: e.serviceDateTo,
			value: e.value,

			sign: e.sign,
			mode: e.mode,
			required: e.required,
			replaceBasePrice: e.replaceBasePrice,
			stopOtherSupplements: e.stopOtherSupplements,
			billingType: e.billingType,
			order: e.order,

			displayName: e.displayName,
			otherPaxsConfig: e.otherPaxsConfig != null
				? e.otherPaxsConfig.map(o => ({
					minPax: o.minPax,
					maxPax: o.maxPax,
					minAge: o.minAge,
					maxAge: o.maxAge,
					minDuration: o.minDuration,
					maxDuration: o.maxDuration,
					checkMatchedPeopleInRestriccions: o.checkMatchedPeopleInRestriccions
				}))
				: null,

			serviceName: e.serviceName,
			bookingDateFromText: e.bookingDateFromText,
			bookingDateToText: e.bookingDateToText,
			serviceDateFromText: e.serviceDateFromText,
			serviceDateToText: e.serviceDateToText
		}));
		const otherMarketItems = price.elements.filter(e => e.market != this.market);
		const contractMarket = this.market
			? contract.markets.find(e => e.marketId == this.market)
			: null;

		if (cost.structure.mode != 'Percent' && items.length > 0) {
			const fromCurrency = cost.elements[0].currency;
			const toCurrency = contractMarket
				? contractMarket.marketCurrencyId
				: priceCurrency.id;
			if (fromCurrency != toCurrency) {
				const toConvert = items.filter(e => this.market == e.market && e.mode == 'Amount').map(e => e.value);
				if (price.structure.currencyExchangeRate == null) {
					this.props.setBusy(true);
					try {
						const response = await this.http.post('api/v1/currencies/exchange-rates', {
							fromCurrencyId: fromCurrency,
							toCurrencyId: toCurrency,
							amounts: toConvert,
							mode: 'Default',
							bookingDate: null,
							serviceDate: null
						});
						const result: {
							rate: number,
							amount: number,
							fromCurrencyId: number,
							toCurrencyId: number
						}[] = response.data;
						price.structure.currencyExchangeRate = result.slice(0, result.length == toConvert.length ? 1 : 2).map(e => e.rate).reduce((prev, curr) => prev * curr, 1);
						price.elements = this.generatePriceElements(contractMarket, items, otherMarketItems, price.structure.defaultMargin, result.map(e => e.amount), toCurrency);
						this.props.setBusy(false);
					} catch (e) {
						this.props.setBusy(false);
						this.toast.showToast('Error!', ObjectUtils.parseError(e), 'error');
					}
				} else {
					const amounts: number[] = [];
					for (const i of toConvert) {
						amounts.push(i * price.structure.currencyExchangeRate);
					}
					price.elements = this.generatePriceElements(contractMarket, items, otherMarketItems, price.structure.defaultMargin, amounts, toCurrency);
				}
			} else {
				price.elements = this.generatePriceElements(contractMarket, items, otherMarketItems, price.structure.defaultMargin);
			}
		} else {
			price.elements = items;
		}
	}

	private generatePriceElements(contractMarket: ContractMarketItem, items: PriceElement[], otherMarketItems: PriceElement[], defaultMargin: number, amounts?: number[], currency?: number) {
		const result: PriceElement[] = [];
		let index = 0;
		for (const item of items) {
			let margin = defaultMargin;
			const value = parseFloat(item.value.toString());
			if (margin == null || margin.toString() == '' || (item.mode == 'Percent')) {
				margin = 0;
			}
			item.value = value + (value * margin);
			if (amounts != null && item.mode != 'Percent') {
				item.value = amounts[index] + amounts[index++] * margin;
			}
			if (item.mode != 'Percent') {
				if (contractMarket) {
					switch (contractMarket.marketMarketPriceType) {
						case 0:
							item.value = Math.ceil(item.value);
							break;
						case 1:
							item.value = Math.floor(item.value);
							break;
						case 2:
							const valueAsString = item.value.toString();
							const pointIndex = valueAsString.indexOf('.');
							if (pointIndex != -1)
								item.value = parseFloat(item.value.toPrecision(pointIndex + 2));
							break;
					}
				} else {
					item.value = Math.ceil(item.value);
				}
			}

			if (currency != null) {
				item.currency = currency;
			}
			result.push(item);
		}

		return otherMarketItems.concat(result);
	}

	@autobind
	private onMarketChange(newVal: number) {
		this.selectedMarket = newVal;
	}

	render() {
		if (this.props.model == null || this.state.schema == null) {
			return null;
		}
		
		const { isValidGeneralInfo, schema } = this.state;
		return (
			<ContractFormView
				isDirty={this.props.isDirty}
				identityService={this.props.identityService}
				defaultLanguaje={this.props.defaultLanguaje}
				mode={this.props.mode}
				onModeChanged={this.props.onModeChanged}
				setBusy={this.props.setBusy}
				formatService={this.props.formatService}
				contextService={this.props.contextService}
				model={this.props.model}
				product={this.props.product}
				supplierType={this.props.product == null || (this.props.product as any).productType != 2 ? 0 : 1}
				currencies={this.props.currencies}
				generatePrices={this.generatePrices}
				selectedMarket={this.selectedMarket}
				onMarketChange={this.onMarketChange}
				breadcrumb={this.props.breadcrumb}
				isNew={this.isNew()}
				isValidGeneralInfo={isValidGeneralInfo}
				onValidateGeneralInfo={this.handleGeneralInfoValidationChecking}
				schema={schema}
				
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/products/contract/form/contract-form.controller.tsx